import React, { useState, useEffect } from "react";
import {
  grey600,
} from "../../lib/rebrandColors/colors";
import { CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import ApplianceTypeDetailsScreen from "../applianceTypeDetails";

export default function ApplianceTypeList({
  layoutProps,
  height,
  width,
  buildingOutlets,
  buildingID,
  applianceTypeFilter,
  filterDirection,
  displayExtra,
  searchTerm,
}) {
  const [filterLoading, setFilterLoading] = useState(true);
  const [filteredOutlets, setFilteredOutlets] = useState(buildingOutlets || layoutProps.outlets);
  const [orderedOutlets, setOrderedOutlets] = useState(buildingOutlets || layoutProps.outlets);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal visibility state
  const [selectedApplianceType, setSelectedApplianceType] = useState({});
  const applianceClick = (applianceType) => {
    setIsModalOpen(true);
    setSelectedApplianceType(applianceType);
  };
  const applianceClickClose = (applianceType) => {
    setIsModalOpen(false);
    setSelectedApplianceType();
  };
  useEffect(() => {
    const adjustOutlets = async () => {
      setFilterLoading(true);

      const sumValuesById = (array) => {
        let result = [];
        if (array.length > 0) {
          result = array.reduce((acc, { outletID, costSavings }) => {
            if (acc[outletID]) {
              acc[outletID].costSavings += costSavings;
            } else {
              acc[outletID] = { outletID, costSavings };
            }
            return acc;
          }, {});
        }

        return Object.values(result);
      };

      const sumSavings = (buildingOutlets && buildingOutlets.length > 0) ?
        sumValuesById(layoutProps.savings.filter(
          (outlet) => buildingOutlets.includes(outlet.outletID)
        )) :
        buildingID ? sumValuesById([]) :
          sumValuesById(layoutProps.savings);
      const idToSavingsMap = new Map(
        sumSavings.map((obj) => [obj.outletID, obj.costSavings])
      );

      const sumByCategory = (buildingOutlets && buildingOutlets.length > 0) ?
        layoutProps.outlets.filter(
          (outlet) => buildingOutlets.includes(outlet.id)
        ).reduce((acc, item) => {
          if (acc[item.productType]) {
            acc[item.productType].totalSavings += idToSavingsMap.get(item.id) || 0;
            acc[item.productType].devices.push(item.id);
            acc[item.productType].count += 1;
          } else {
            acc[item.productType] = {
              totalSavings: idToSavingsMap.get(item.id) || 0,
              devices: [item.id],
              count: 1,
            };
          };
          return acc;
        }, {}) :
        layoutProps.outlets.reduce((acc, item) => {
          if (acc[item.productType]) {
            acc[item.productType].totalSavings += idToSavingsMap.get(item.id) || 0;
            acc[item.productType].devices.push(item.id);
            acc[item.productType].count += 1;
          } else {
            acc[item.productType] = {
              totalSavings: idToSavingsMap.get(item.id) || 0,
              devices: [item.id],
              count: 1,
            };
          };
          return acc;
        }, {});

      let groupedCategories = Object.entries(sumByCategory).map(
        ([productType, { totalSavings, devices, count }]) => ({
          productType: productType || "N/A",
          totalSavings,
          devices,
          count,
        })
      );

      if (!(buildingOutlets && buildingOutlets.length > 0) && buildingID) {
        groupedCategories = [];
      } else {
        groupedCategories = groupedCategories.sort((a, b) => b.totalSavings - a.totalSavings);
      };

      setFilteredOutlets(groupedCategories);
      setOrderedOutlets(groupedCategories);
      setFilterLoading(false);
    };

    adjustOutlets();
  }, [layoutProps.outlets, layoutProps.savings]);

  useEffect(() => {
    if (!filterLoading && (location.pathname !== '/' || displayExtra)) {
      let sorted = [...filteredOutlets].sort((a, b) => {
          const nameA = a.productType.toLowerCase(); // to ensure case-insensitive sorting
          const nameB = b.productType.toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
      });
      
      if (applianceTypeFilter === 'Appliance Type' && filterDirection === 'Desc') {
        sorted = [...filteredOutlets].sort((a, b) => {
          const nameA = a.productType.toLowerCase(); // to ensure case-insensitive sorting
          const nameB = b.productType.toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      }
      else if (applianceTypeFilter === 'Appliance Type' && filterDirection === 'Asc') {
        sorted = [...filteredOutlets].sort((a, b) => {
          const nameA = a.productType.toLowerCase(); // to ensure case-insensitive sorting
          const nameB = b.productType.toLowerCase();
          if (nameB < nameA) return -1;
          if (nameB > nameA) return 1;
          return 0;
        });
      } else if (applianceTypeFilter === 'Savings' && filterDirection === 'Desc') {
        sorted = [...filteredOutlets].sort((a, b) => b.totalSavings - a.totalSavings)
      } else if (applianceTypeFilter === 'Savings' && filterDirection === 'Asc') {
        sorted = [...filteredOutlets].sort((a, b) => a.totalSavings - b.totalSavings)
      } else if (applianceTypeFilter === 'Number of Devices' && filterDirection === 'Desc') {
        sorted = [...filteredOutlets].sort((a, b) => b.count - a.count)
      } else if (applianceTypeFilter === 'Number of Devices' && filterDirection === 'Asc') {
        sorted = [...filteredOutlets].sort((a, b) => a.count - b.count)
      };

      if (searchTerm?.length > 0) {
        // Convert searchTerm to lowercase for case-insensitive search
        const term = searchTerm.toLowerCase();

        sorted = [...sorted].filter(outlet =>
          outlet.device?.toLowerCase().includes(term) ||
          outlet.name?.toLowerCase().includes(term) ||
          outlet.productType?.toLowerCase().includes(term)
        );
      }
      setOrderedOutlets(sorted);
    };
  }, [layoutProps.outlets, applianceTypeFilter, filterDirection, filterLoading, searchTerm]);

  return (
    <div style={{
      margin: ".5%",
      minWidth: "500px",
      borderRadius: "24px",
    }}>
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: height,
        alignItems: "center",
        width: "100%",
      }}>
        {filterLoading ? (
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "50px",
          }}>
            <CircularProgress />
          </div>
        ) : orderedOutlets.length > 0 ? (
          orderedOutlets.map((category, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                width: width ? width : "480px",
                minHeight: "88px",
                backgroundColor: "#EEEEEE",
                borderRadius: "16px",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "5px",
                marginTop: i == 0 ? "2px" : "16px",
                cursor: 'pointer',
              }}
              onClick={() => applianceClick(category)}
            >
              <div style={{
                width: "90%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div
                    className="labelMedium"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: grey600,
                    }}
                  >
                    {category.productType}
                  </div>
                  <div
                    className="labelSmall"
                    style={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: grey600,
                    }}
                  >
                    {category.count} Outlets
                  </div>
                </div>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}>
                  <div
                    className="buttonSmall"
                    style={{
                      fontSize: "10px",
                      lineHeight: "10px",
                      textAlign: "center",
                    }}
                  >
                    Estimated
                    <br />
                    savings:
                  </div>
                  <div style={{
                    width: "63px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "24px",
                    backgroundColor: "#FFFFFF",
                    marginTop: "5px",
                  }}>
                    <div
                      className="buttonSmall"
                      style={{
                        color: "#E50914",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                    >
                      ${category.totalSavings.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            className="headlineMedium"
            style={{
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              marginTop: "50px",
              color: "#999999",
            }}
          >
            No appliance types available
          </div>
        )}
      </div>
      {isModalOpen &&
        <ApplianceTypeDetailsScreen
          applianceType={selectedApplianceType}
          layoutProps={layoutProps}
          setSelectedApplianceType={setSelectedApplianceType}
          open={isModalOpen}
          handleClose={applianceClickClose}
        />
      }
    </div>
  );
};
